import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import '../Home/Home.css';
import '../Auth.css';
import {
  formatVnd,
  getOrderedPackageEntries,
  summarizeIncludedFeatures,
} from '../../utils/packageCatalog';
import PasteReviews from '../../components/PasteReviews';

const COUNTRY_TERMS = new Set([
  'australia',
  'canada',
  'france',
  'japan',
  'south korea',
  'united kingdom',
  'united states',
  'usa',
  'us',
  'vietnam',
  'viet nam',
]);

const STREET_HINT_RE = /\b(address|alley|apartment|apt|avenue|ave|block|boulevard|blvd|building|đường|duong|hẻm|hem|lane|lot|ngõ|ngo|phường|phuong|road|rd|street|st|suite|tổ|to|ward)\b/i;
const POSTAL_CODE_RE = /\b\d{4,6}(?:-\d{3,4})?\b/g;

const normalizeSpaces = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const stripPostalCodes = (value) => normalizeSpaces(String(value || '').replace(POSTAL_CODE_RE, ''));

const isCountryPart = (value) => COUNTRY_TERMS.has(normalizeSpaces(value).toLowerCase());

const isStreetAddressPart = (value) => {
  const normalized = normalizeSpaces(value);
  if (!normalized) return false;
  if (/^\d+[\w/-]?\b/.test(normalized)) return true;
  return STREET_HINT_RE.test(normalized);
};

export const buildBusinessSearchLocation = (address) => {
  const parts = String(address || '')
    .split(',')
    .map(stripPostalCodes)
    .filter(Boolean)
    .filter((part) => !isCountryPart(part));

  if (!parts.length) return '';

  const localityParts = parts.filter((part) => !isStreetAddressPart(part));
  if (localityParts.length) return localityParts[localityParts.length - 1];

  return parts[parts.length - 1];
};

export const buildBusinessSearchQuery = ({ name, address } = {}) => {
  const normalizedName = normalizeSpaces(name);
  const location = buildBusinessSearchLocation(address);
  return [normalizedName, location].filter(Boolean).join(' ');
};

export default function Onboarding() {
  const [step, setStep] = useState('find');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [buildProgress, setBuildProgress] = useState('');
  const [buildDone, setBuildDone] = useState(false);
  const [placesDone, setPlacesDone] = useState(false);
  const [contentDone, setContentDone] = useState(false);
  const [packages, setPackages] = useState(null);
  const [selectedTier, setSelectedTier] = useState('standard');
  const logoFileInputRef = useRef(null);

  const [siteData, setSiteData] = useState({});
  const [reviewsJson, setReviewsJson] = useState([]);
  const [photosJson, setPhotosJson] = useState([]);
  const [reviewSummary, setReviewSummary] = useState('');
  const [reviewQuotes, setReviewQuotes] = useState([]);
  const [marketingKit, setMarketingKit] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPass, setWifiPass] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [pinterestUrl, setPinterestUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [mixcloudUrl, setMixcloudUrl] = useState('');
  const [appleMusicUrl, setAppleMusicUrl] = useState('');
  const [tripadvisorUrl, setTripadvisorUrl] = useState('');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [menuUrl, setMenuUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [socialStats, setSocialStats] = useState({});
  const [productsAndServices, setProductsAndServices] = useState([]);
  const history = useHistory();

  const ensureSiteId = async () => {
    const existingSiteId = localStorage.getItem('siteId');
    if (existingSiteId && existingSiteId !== 'undefined' && existingSiteId !== 'null') return existingSiteId;

    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const sitesRes = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const firstSiteId = sitesRes.data?.sites?.[0]?.id;
      if (firstSiteId) {
        localStorage.setItem('siteId', String(firstSiteId));
        return String(firstSiteId);
      }
    } catch (err) {
      console.error('Failed to load sites', err.response?.data || err.message);
    }

    const res = await axios.post(
      '/api/sites',
      { name: 'Main Site' },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newSiteId = res.data?.siteId;
    if (!newSiteId) return null;
    localStorage.setItem('siteId', String(newSiteId));
    return String(newSiteId);
  };

  const goToSiteDetails = async (placeId, ensuredSiteId = null) => {
    setMessage('');
    const finalSiteId = ensuredSiteId || siteId || (await ensureSiteId());

    if (!finalSiteId) {
      setMessage('Could not create or find a site for your account. Please try again.');
      return;
    }

    const params = new URLSearchParams();
    params.set('tab', 'Settings');
    params.set('settingsSubTab', 'Site Details');
    params.set('siteId', String(finalSiteId));
    if (placeId) params.set('placeId', placeId);
    history.push(`/control-panel?${params.toString()}`);
  };

  const goToShortcuts = async (ensuredSiteId = null) => {
    setMessage('');
    const finalSiteId = ensuredSiteId || siteId || (await ensureSiteId());
    if (!finalSiteId) {
      setMessage('Could not create or find a site for your account. Please try again.');
      return;
    }

    const params = new URLSearchParams();
    params.set('tab', 'Settings');
    params.set('settingsSubTab', 'Shortcuts');
    params.set('siteId', String(finalSiteId));
    history.push(`/control-panel?${params.toString()}`);
  };

  const safeJsonParse = (value, fallback = null) => {
    if (!value) return fallback;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const loadSiteFromDb = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const res = await axios.get('/api/sites', { headers: { Authorization: `Bearer ${token}` } });
    const site = (res.data?.sites || []).find((s) => String(s.id) === String(id));
    if (!site) return null;

    setSiteData({
      name: site.name || '',
      address: site.address || '',
      phone: site.phone || '',
      website: site.website || '',
      place_id: site.place_id || '',
      google_url: site.google_url || '',
      lat: site.lat,
      lng: site.lng,
      rating: site.rating,
      ratings_total: site.ratings_total,
      business_type: site.business_type,
      primary_type: site.primary_type,
      primary_type_display_name: site.primary_type_display_name,
      opening_hours_json: safeJsonParse(site.opening_hours_json, null),
      photos_json: safeJsonParse(site.photos_json, []),
      reviews_json: safeJsonParse(site.reviews_json, []),
    });

    setReviewsJson(safeJsonParse(site.reviews_json, []));
    setPhotosJson(safeJsonParse(site.photos_json, []));
    setWifiSsid(site.wifi_ssid || '');
    setWifiPass(site.wifi_password || '');
    setFacebookUrl(site.facebook_url || '');
    setInstagramUrl(site.instagram_url || '');
    setTwitterUrl(site.twitter_url || '');
    setPinterestUrl(site.pinterest_url || '');
    setTiktokUrl(site.tiktok_url || '');
    setYoutubeUrl(site.youtube_url || '');
    setSpotifyUrl(site.spotify_url || '');
    setSoundcloudUrl(site.soundcloud_url || '');
    setMixcloudUrl(site.mixcloud_url || '');
    setAppleMusicUrl(site.apple_music_url || '');
    setTripadvisorUrl(site.tripadvisor_url || '');
    setGoogleReviewUrl(site.google_review_url || '');
    setMenuUrl(site.menu_url || '');
    setLogoUrl(site.logo_url || '');
    setBusinessDescription(site.business_description || '');
    setSocialStats(safeJsonParse(site.social_media_stats, {}) || {});
    setProductsAndServices(safeJsonParse(site.packages_json, []) || []);
    setReviewSummary(site.review_summary || '');
    setReviewQuotes(safeJsonParse(site.review_quotes, []) || []);
    setMarketingKit(site.marketing_kit || '');
    return site;
  };

  const getUrlToOpen = (rawUrl) => {
    const trimmed = String(rawUrl || '').trim();
    if (!trimmed) return null;
    try {
      return new URL(trimmed).toString();
    } catch {
      // ignore
    }
    try {
      return new URL(`https://${trimmed}`).toString();
    } catch {
      // ignore
    }
    return null;
  };

  const handleCheckUrl = (rawUrl) => {
    const urlToOpen = getUrlToOpen(rawUrl);
    if (!urlToOpen) {
      alert('Please enter a valid URL first.');
      return;
    }
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };

  const openGoogleSearch = (queryText) => {
    const q = String(queryText || '').trim();
    if (!q) return;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(q)}`, '_blank', 'noopener,noreferrer');
  };

  const openGoogleImagesSearch = (queryText) => {
    const q = String(queryText || '').trim();
    if (!q) return;
    window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(q)}`, '_blank', 'noopener,noreferrer');
  };

  const openMapsSearch = (queryText) => {
    const q = String(queryText || '').trim();
    if (!q) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, '_blank', 'noopener,noreferrer');
  };

  const getBusinessQueryBase = () => {
    const name = siteData?.name || '';
    const address = siteData?.address || '';
    return `${name} ${address}`.trim();
  };

  const getBusinessOnlineSearchBase = () => buildBusinessSearchQuery({
    name: siteData?.name || '',
    address: siteData?.address || '',
  }) || getBusinessQueryBase();

  const handleUploadLogoClick = () => {
    if (!logoFileInputRef.current) return;
    logoFileInputRef.current.value = '';
    logoFileInputRef.current.click();
  };

  const handleLogoFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!siteId) {
      alert('No site selected.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You must be logged in to upload a logo.');
      return;
    }

    setBuildProgress('Uploading logo');
    try {
      const presignRes = await axios.get(`/api/sites/${siteId}/logo/presign`, {
        params: { filename: file.name },
        headers: { Authorization: `Bearer ${token}` },
      });
      const { url, key } = presignRes.data || {};
      if (!url || !key) throw new Error('Invalid presign response');

      const putRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

      const finalizeRes = await axios.post(
        `/api/sites/${siteId}/logo/finalize`,
        { key, filename: file.name, fileType: file.type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newLogoUrl = finalizeRes.data?.logoUrl || '';
      if (!newLogoUrl) throw new Error('Logo upload finalized but no URL returned');
      setLogoUrl(newLogoUrl);
    } catch (err) {
      console.error('Logo upload failed', err.response?.data || err.message);
      alert('Failed to upload logo.');
    } finally {
      setBuildProgress('');
    }
  };

  const searchPlaces = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setMessage('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/places/search', {
        params: { q: trimmed, _ts: Date.now() },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setResults(res.data);
    } catch (err) {
      console.error('places search failed', err.response?.data || err.message);
      setMessage('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/entitlements/packages', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackages(res.data || null);
    } catch (err) {
      console.error('Failed to fetch packages', err.response?.data || err.message);
      setPackages(null);
    }
  };

  const runBackgroundSiteBuild = async ({ placeId, ensuredSiteId }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMessage('Missing auth token. Please log in again.');
      setBuildDone(true);
      return;
    }

    setPlacesDone(false);
    setContentDone(false);
    setBuildDone(false);

    if (!placeId || placeId === 'manual' || placeId === 'existing') {
      await loadSiteFromDb(ensuredSiteId);
      setBuildProgress('');
      setPlacesDone(true);
      setContentDone(true);
      setBuildDone(true);
      return;
    }

    try {
      setBuildProgress('Searching Google Maps');
      const importRes = await axios.post(
        `/api/sites/${ensuredSiteId}/import-business`,
        { placeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSiteData(importRes.data?.siteData || {});
      setReviewsJson(Array.isArray(importRes.data?.reviewsJson) ? importRes.data.reviewsJson : []);
      setPhotosJson(Array.isArray(importRes.data?.photosJson) ? importRes.data.photosJson : []);
      setReviewSummary('');
      setReviewQuotes([]);
      setMarketingKit('');
      setPlacesDone(true);

      const importedName = importRes.data?.siteData?.name;
      if (importedName) {
        setBuildProgress('Creating your site');
        await axios.put(
          `/api/sites/${ensuredSiteId}`,
          { name: importedName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setBuildProgress('Fetching extra info');
      try {
        const infoRes = await axios.post(
          `/api/sites/${ensuredSiteId}/fetch-extra-info`,
          {
            name: importRes.data?.siteData?.name,
            location:
              importRes.data?.siteData?.address ||
              `${importRes.data?.siteData?.lat},${importRes.data?.siteData?.lng}`,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setInstagramUrl(infoRes.data?.instagram_url || '');
        setTwitterUrl(infoRes.data?.twitter_url || '');
        setPinterestUrl(infoRes.data?.pinterest_url || '');
        setTiktokUrl(infoRes.data?.tiktok_url || '');
        setYoutubeUrl(infoRes.data?.youtube_url || '');
        setSpotifyUrl(infoRes.data?.spotify_url || '');
        setSoundcloudUrl(infoRes.data?.soundcloud_url || '');
        setMixcloudUrl(infoRes.data?.mixcloud_url || '');
        setAppleMusicUrl(infoRes.data?.apple_music_url || '');
        setTripadvisorUrl(infoRes.data?.tripadvisor_url || '');
        setMenuUrl(infoRes.data?.menu_url || '');
        setLogoUrl(infoRes.data?.logo_url || '');
        setFacebookUrl(infoRes.data?.facebook_url || '');
        setSocialStats(infoRes.data?.social_media_stats || {});
        setBusinessDescription((prev) => infoRes.data?.business_description || prev);
      } catch (err) {
        console.error('fetch-extra-info failed', err.response?.data || err.message);
      }

      setBuildProgress('Generating review summary');
      try {
        const summaryRes = await axios.post(
          `/api/sites/${ensuredSiteId}/regenerate-review-summary`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setReviewSummary(summaryRes.data?.reviewSummary || '');
        setReviewQuotes(summaryRes.data?.reviewQuotes || []);
      } catch (err) {
        console.error('regenerate-review-summary failed', err.response?.data || err.message);
      }

      setBuildProgress('Generating marketing kit');
      try {
        const mkRes = await axios.post(
          `/api/sites/${ensuredSiteId}/regenerate-marketing-kit`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMarketingKit(mkRes.data?.marketingKit || '');
        setContentDone(true);
      } catch (err) {
        console.error('regenerate-marketing-kit failed', err.response?.data || err.message);
      }
    } finally {
      setBuildProgress('');
      setBuildDone(true);
    }
  };

  const startChoosePackage = async (placeId) => {
    setSelectedPlaceId(placeId);
    setStep('package');
    setMessage('');
    setBuildDone(false);
    setBuildProgress(placeId === 'manual' ? '' : 'Preparing your site');

    let ensuredSiteId = null;
    try {
      ensuredSiteId = await ensureSiteId();
      setSiteId(String(ensuredSiteId));
    } catch (err) {
      console.error('ensureSiteId failed', err.response?.data || err.message);
    }

    fetchPackages();
    if (ensuredSiteId) {
      setTimeout(() => runBackgroundSiteBuild({ placeId, ensuredSiteId }), 0);
    } else {
      setBuildDone(true);
    }
  };

  const packageCards = useMemo(
    () => getOrderedPackageEntries(packages?.packageCatalog),
    [packages]
  );

  const saveSitePackage = async (targetSiteId, packageKey) => {
    const token = localStorage.getItem('token');
    if (!token || !targetSiteId) return;
    const res = await axios.put(
      `/api/sites/${targetSiteId}/package`,
      { packageKey },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data || null;
  };

  const submitPackage = async () => {
    if (!siteId) {
      setMessage('Could not create or find a site for your account. Please try again.');
      return;
    }
    if (!selectedPlaceId) return;

    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const packageSave = await saveSitePackage(siteId, selectedTier);
      const res = await axios.put(
        '/api/users/me/subscription',
        { subscriptionType: packageSave?.effectiveSubscriptionType || selectedTier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
      }
      setStep('business');
    } catch (err) {
      console.error('Failed to set subscription', err.response?.data || err.message);
      setMessage(err.response?.data?.message || 'Failed to save subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveAllAndFinish = async () => {
    if (!siteId) return;
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/sites/${siteId}/confirm-business`,
        {
          siteData,
          reviewsJson,
          photosJson,
          reviewSummary,
          reviewQuotes,
          marketingKit,
          wifi_ssid: wifiSsid || null,
          wifi_password: wifiPass || null,
          facebook_url: facebookUrl || null,
          instagram_url: instagramUrl || null,
          twitter_url: twitterUrl || null,
          pinterest_url: pinterestUrl || null,
          tiktok_url: tiktokUrl || null,
          youtube_url: youtubeUrl || null,
          spotify_url: spotifyUrl || null,
          soundcloud_url: soundcloudUrl || null,
          mixcloud_url: mixcloudUrl || null,
          apple_music_url: appleMusicUrl || null,
          tripadvisor_url: tripadvisorUrl || null,
          google_review_url: googleReviewUrl || null,
          menu_url: menuUrl || null,
          logo_url: logoUrl || null,
          business_description: businessDescription || null,
          social_media_stats: socialStats,
          packages: productsAndServices,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await goToShortcuts(siteId);
    } catch (err) {
      console.error('Failed to save onboarding data', err.response?.data || err.message);
      setMessage(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 'package') return;
    if (!selectedTier) setSelectedTier('standard');
  }, [selectedTier, step]);

  return (
    <div className="auth-page">
      {step === 'find' && (
        <div className="auth-card space-y-4" style={{ background: '#0b1220', border: '1px solid #334155', color: '#e5e7eb' }}>
          <h1 className="text-2xl font-bold text-center">Find your business</h1>
          <div className="flex space-x-2">
            <input
              type="text"
              className="border p-2 flex-1"
              placeholder="Business name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
            />
            <button
              onClick={searchPlaces}
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={loading}
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
          <ul
            className="max-h-48 overflow-y-auto rounded"
            style={{ backgroundColor: '#111827', color: '#e5e7eb', border: '1px solid #334155' }}
          >
            {results.map((r) => (
              <li
                key={r.place_id}
                className="py-2 px-3 hover:bg-gray-100 cursor-pointer"
                style={{ cursor: 'pointer' }}
                onClick={() => startChoosePackage(r.place_id)}
              >
                {r.description || r.place_id}
              </li>
            ))}
            {!results.length && (
              <li className="py-3 px-3 text-sm" style={{ color: '#9ca3af' }}>
                Search for your venue to auto-fill details, or skip and enter manually.
              </li>
            )}
          </ul>
          {message && <p className="text-sm">{message}</p>}
          <button
            onClick={() => startChoosePackage('manual')}
            className="bg-gray-300 text-black px-4 py-2 rounded w-full"
            disabled={loading}
          >
            {loading ? 'Please wait…' : 'Skip'}
          </button>
        </div>
      )}

      {step === 'package' && (
        <div
          className="rounded-xl p-5 space-y-4"
          style={{ width: 'min(980px, 100%)', background: '#0b1220', border: '1px solid #334155', color: '#e5e7eb' }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Choose your package</h1>
              <p className="text-sm" style={{ color: '#9ca3af', marginTop: 6 }}>
                Standard is selected by default. You can change this later (admin tools).
              </p>
            </div>
            <button
              type="button"
              className="rounded px-3 py-2 text-sm"
              style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
              onClick={() => {
                setStep('find');
                setResults([]);
                setQuery('');
              }}
              disabled={loading}
            >
              Back
            </button>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {packageCards.map((pkg) => {
              const tier = String(pkg?.key || '').toLowerCase();
              const isSelected = selectedTier === tier;
              const includedSummary = summarizeIncludedFeatures(pkg);
              return (
                <button
                  key={pkg.key}
                  type="button"
                  onClick={() => setSelectedTier(tier)}
                  className="rounded-xl p-4 text-left"
                  style={{
                    background: isSelected ? 'rgba(99,102,241,0.18)' : 'rgba(15,23,42,0.5)',
                    border: isSelected ? '1px solid rgba(99,102,241,0.7)' : '1px solid #334155',
                    color: '#e5e7eb',
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div style={{ fontSize: 12, letterSpacing: 1.2, color: '#9ca3af', fontWeight: 700 }}>
                        {String(pkg.label || tier).toUpperCase()}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                        {pkg.isPoa ? 'POA' : `${formatVnd(pkg.basePrice)} VND`}
                        {!pkg.isPoa && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}> /month</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isSelected ? '#a5b4fc' : '#94a3b8' }}>
                      {isSelected ? 'Selected' : 'Select'}
                    </div>
                  </div>

                  <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: 13, color: '#cbd5e1' }}>{pkg.summary}</div>
                    <div style={{ fontSize: 13, color: '#94a3b8' }}>
                      {includedSummary || 'No included items configured'}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      Website: {pkg.websitePolicy}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      AI: {pkg.aiPolicy}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      API: {pkg.apiPolicy}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {message && <p className="text-sm">{message}</p>}

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm" style={{ color: '#9ca3af' }}>
              {buildProgress ? `Building your site in the background: ${buildProgress}…` : buildDone ? 'Background tasks finished.' : 'Building your site in the background…'}
            </div>
            <button
              type="button"
              className="rounded px-4 py-2 text-sm font-semibold text-white"
              style={{ background: '#4f46e5' }}
              onClick={submitPackage}
              disabled={loading}
            >
              {loading ? 'Saving…' : 'Next'}
            </button>
          </div>
        </div>
      )}

      {step === 'business' && (
        <div
          className="rounded-xl p-5 space-y-4"
          style={{ width: 'min(980px, 100%)', background: '#0b1220', border: '1px solid #334155', color: '#e5e7eb' }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Confirm business details</h1>
              <p className="text-sm" style={{ color: '#9ca3af', marginTop: 6 }}>
                Review and tweak what we found. Background enrichment continues below.
              </p>
            </div>
            <button
              type="button"
              className="rounded px-3 py-2 text-sm"
              style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
              onClick={() => setStep('package')}
              disabled={loading}
            >
              Back
            </button>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">Name</div>
              <input
                className="border p-2 w-full rounded"
                style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                value={siteData.name || ''}
                onChange={(e) => setSiteData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </label>
            <label className="block">
              <div className="text-sm font-semibold">Phone</div>
              <input
                className="border p-2 w-full rounded"
                style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                value={siteData.phone || ''}
                onChange={(e) => setSiteData((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </label>
          </div>

          <label className="block">
            <div className="text-sm font-semibold">Address</div>
            <input
              className="border p-2 w-full rounded"
              style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
              value={siteData.address || ''}
              onChange={(e) => setSiteData((prev) => ({ ...prev, address: e.target.value }))}
            />
          </label>

          <label className="block">
            <div className="text-sm font-semibold">Website</div>
            <div className="flex gap-2">
              <input
                className="border p-2 w-full rounded"
                style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                value={siteData.website || ''}
                onChange={(e) => setSiteData((prev) => ({ ...prev, website: e.target.value }))}
              />
              <button
                type="button"
                className="rounded px-3"
                style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                onClick={() => {
                  if (getUrlToOpen(siteData.website)) return handleCheckUrl(siteData.website);
                  return openGoogleSearch(`${getBusinessOnlineSearchBase()} website`);
                }}
              >
                {getUrlToOpen(siteData.website) ? 'Check' : 'Search'}
              </button>
            </div>
          </label>

          <label className="block">
            <div className="text-sm font-semibold">Google Maps URL (optional)</div>
            <div className="flex gap-2">
              <input
                className="border p-2 w-full rounded"
                style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                value={siteData.google_url || ''}
                onChange={(e) => setSiteData((prev) => ({ ...prev, google_url: e.target.value }))}
              />
              <button
                type="button"
                className="rounded px-3"
                style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                onClick={() => {
                  if (getUrlToOpen(siteData.google_url)) return handleCheckUrl(siteData.google_url);
                  return openMapsSearch(getBusinessQueryBase());
                }}
              >
                {getUrlToOpen(siteData.google_url) ? 'Check' : 'Search'}
              </button>
            </div>
          </label>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">Facebook URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(facebookUrl)) return handleCheckUrl(facebookUrl);
                    return openGoogleSearch(`site:facebook.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(facebookUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-sm font-semibold">Instagram URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(instagramUrl)) return handleCheckUrl(instagramUrl);
                    return openGoogleSearch(`site:instagram.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(instagramUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">Twitter URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={twitterUrl}
                  onChange={(e) => setTwitterUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(twitterUrl)) return handleCheckUrl(twitterUrl);
                    return openGoogleSearch(`${getBusinessOnlineSearchBase()} twitter`);
                  }}
                >
                  {getUrlToOpen(twitterUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-sm font-semibold">Pinterest URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={pinterestUrl}
                  onChange={(e) => setPinterestUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(pinterestUrl)) return handleCheckUrl(pinterestUrl);
                    return openGoogleSearch(`${getBusinessOnlineSearchBase()} pinterest`);
                  }}
                >
                  {getUrlToOpen(pinterestUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">TikTok URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={tiktokUrl}
                  onChange={(e) => setTiktokUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(tiktokUrl)) return handleCheckUrl(tiktokUrl);
                    return openGoogleSearch(`site:tiktok.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(tiktokUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-sm font-semibold">YouTube URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(youtubeUrl)) return handleCheckUrl(youtubeUrl);
                    return openGoogleSearch(`site:youtube.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(youtubeUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">Spotify URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={spotifyUrl}
                  onChange={(e) => setSpotifyUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(spotifyUrl)) return handleCheckUrl(spotifyUrl);
                    return openGoogleSearch(`site:spotify.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(spotifyUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-sm font-semibold">SoundCloud URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={soundcloudUrl}
                  onChange={(e) => setSoundcloudUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(soundcloudUrl)) return handleCheckUrl(soundcloudUrl);
                    return openGoogleSearch(`site:soundcloud.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(soundcloudUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">Mixcloud URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={mixcloudUrl}
                  onChange={(e) => setMixcloudUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(mixcloudUrl)) return handleCheckUrl(mixcloudUrl);
                    return openGoogleSearch(`site:mixcloud.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(mixcloudUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-sm font-semibold">Apple Music URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={appleMusicUrl}
                  onChange={(e) => setAppleMusicUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(appleMusicUrl)) return handleCheckUrl(appleMusicUrl);
                    return openGoogleSearch(`site:music.apple.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(appleMusicUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <label className="block">
              <div className="text-sm font-semibold">TripAdvisor URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={tripadvisorUrl}
                  onChange={(e) => setTripadvisorUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(tripadvisorUrl)) return handleCheckUrl(tripadvisorUrl);
                    return openGoogleSearch(`site:tripadvisor.com ${getBusinessOnlineSearchBase()}`);
                  }}
                >
                  {getUrlToOpen(tripadvisorUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-sm font-semibold">Google Review URL (optional)</div>
              <div className="flex gap-2">
                <input
                  className="border p-2 w-full rounded"
                  style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                  value={googleReviewUrl}
                  onChange={(e) => setGoogleReviewUrl(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded px-3"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => {
                    if (getUrlToOpen(googleReviewUrl)) return handleCheckUrl(googleReviewUrl);
                    return openGoogleSearch(`${getBusinessOnlineSearchBase()} google reviews`);
                  }}
                >
                  {getUrlToOpen(googleReviewUrl) ? 'Check' : 'Search'}
                </button>
              </div>
            </label>
          </div>

          <label className="block">
            <div className="text-sm font-semibold">Menu URL (optional)</div>
            <div className="flex gap-2">
              <input
                className="border p-2 w-full rounded"
                style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
                value={menuUrl}
                onChange={(e) => setMenuUrl(e.target.value)}
              />
              <button
                type="button"
                className="rounded px-3"
                style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                onClick={() => {
                  if (getUrlToOpen(menuUrl)) return handleCheckUrl(menuUrl);
                  return openGoogleSearch(`${getBusinessOnlineSearchBase()} menu`);
                }}
              >
                {getUrlToOpen(menuUrl) ? 'Check' : 'Search'}
              </button>
            </div>
          </label>

          <div className="rounded-xl p-4" style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid #334155' }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Logo</div>
                <div className="text-xs" style={{ color: '#9ca3af' }}>
                  Upload a logo now, or add it later in Site Details.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded px-3 py-2 text-sm"
                  style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
                  onClick={() => openGoogleImagesSearch(`${getBusinessOnlineSearchBase()} logo`)}
                >
                  Search
                </button>
                <button
                  type="button"
                  className="rounded px-3 py-2 text-sm"
                  style={{ background: '#4f46e5', color: '#fff' }}
                  onClick={handleUploadLogoClick}
                >
                  Upload
                </button>
              </div>
            </div>
            {logoUrl && (
              <div className="mt-3">
                <img alt="Logo" src={logoUrl} style={{ maxHeight: 96, maxWidth: 260, objectFit: 'contain' }} />
              </div>
            )}
            <input
              ref={logoFileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleLogoFileSelected}
            />
          </div>

          {message && <p className="text-sm">{message}</p>}

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm" style={{ color: '#9ca3af' }}>
              {buildProgress ? `Building your site in the background: ${buildProgress}…` : contentDone ? 'Background tasks finished.' : placesDone ? 'Generating AI content…' : 'Fetching business details…'}
            </div>
            <button
              type="button"
              className="rounded px-4 py-2 text-sm font-semibold text-white"
              style={{ background: contentDone ? '#4f46e5' : '#334155', cursor: contentDone ? 'pointer' : 'not-allowed' }}
              onClick={() => setStep('content')}
              disabled={!contentDone || loading}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {step === 'content' && (
        <div
          className="rounded-xl p-5 space-y-4"
          style={{ width: 'min(980px, 100%)', background: '#0b1220', border: '1px solid #334155', color: '#e5e7eb' }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Review your marketing kit</h1>
              <p className="text-sm" style={{ color: '#9ca3af', marginTop: 6 }}>
                Review the AI-generated content and tweak it to match your venue. Everything will be saved before you continue.
              </p>
            </div>
            <button
              type="button"
              className="rounded px-3 py-2 text-sm"
              style={{ background: '#111827', border: '1px solid #334155', color: '#e5e7eb' }}
              onClick={() => setStep('business')}
              disabled={loading}
            >
              Back
            </button>
          </div>

          {!contentDone && (
            <div className="rounded p-3 text-sm" style={{ border: '1px solid #334155', background: 'rgba(15,23,42,0.5)', color: '#cbd5e1' }}>
              Background tasks are still running. Please wait… {buildProgress ? `(${buildProgress})` : ''}
            </div>
          )}

          <label className="block">
            <div className="text-sm font-semibold">Marketing kit</div>
            <textarea
              className="border p-3 w-full rounded"
              style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
              rows={10}
              value={marketingKit}
              onChange={(e) => setMarketingKit(e.target.value)}
              disabled={!contentDone}
            />
          </label>

          <label className="block">
            <div className="text-sm font-semibold">Review summary</div>
            <textarea
              className="border p-3 w-full rounded"
              style={{ background: '#111827', borderColor: '#334155', color: '#e5e7eb' }}
              rows={6}
              value={reviewSummary}
              onChange={(e) => setReviewSummary(e.target.value)}
              disabled={!contentDone}
            />
          </label>

          <PasteReviews
            siteId={siteId}
            disabled={!contentDone}
            onImported={(data) => {
              setReviewsJson(Array.isArray(data.reviewsJson) ? data.reviewsJson : []);
              setReviewSummary(data.reviewSummary || '');
              setReviewQuotes(Array.isArray(data.reviewQuotes) ? data.reviewQuotes : []);
              setMarketingKit(data.marketingKit || '');
            }}
          />

          <details
            className="rounded-xl p-4"
            style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid #334155' }}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 800 }}>
              View reviews ({Array.isArray(reviewsJson) ? reviewsJson.length : 0})
            </summary>
            <div className="mt-3 space-y-2">
              {Array.isArray(reviewsJson) && reviewsJson.length ? (
                reviewsJson.slice(0, 20).map((r, idx) => (
                  <div key={idx} className="rounded p-2" style={{ background: '#111827', border: '1px solid #334155' }}>
                    <div className="text-xs" style={{ color: '#9ca3af' }}>
                      {r?.authorAttribution?.displayName || r?.author_name || 'Anonymous'}
                    </div>
                    <div className="text-sm" style={{ color: '#e5e7eb' }}>
                      {r?.originalText?.text || r?.text?.text || ''}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm" style={{ color: '#9ca3af' }}>
                  No reviews found yet.
                </div>
              )}
            </div>
          </details>

          {message && <p className="text-sm">{message}</p>}

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm" style={{ color: '#9ca3af' }}>
              {buildProgress ? `Building your site in the background: ${buildProgress}…` : contentDone ? 'Ready to finish.' : 'Building your site in the background…'}
            </div>
            <button
              type="button"
              className="rounded px-4 py-2 text-sm font-semibold text-white"
              style={{ background: contentDone ? '#4f46e5' : '#334155', cursor: contentDone ? 'pointer' : 'not-allowed' }}
              onClick={saveAllAndFinish}
              disabled={!contentDone || loading}
            >
              {loading ? 'Saving…' : 'Finish'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
